import type { DynamicGroup as DynamicGroupType } from "@/models/new/forms.types"
import type { Responses } from "@/models/new/formResponse.types"

import { getDynamicGroupInstances } from "../shared/attachments/attachment-groups"

export { getDynamicGroupInstances }

/**
 * Reindex `responses` after a dynamic-group instance is removed using
 * position-compaction: every surviving detected instance is renumbered to its
 * position (index + 1) in the remaining sorted set, compacting the instances to a
 * contiguous `1..N` range.
 *
 * `remainingInstances` is the canonical surviving set (the detected instances with
 * `deletedInstance` already removed), shared with the `fieldMedia` and
 * `creationMedia` reindexers so all three maps end up keyed to the same instance
 * numbers. When omitted, it is derived from `responses` so the function stays usable
 * in isolation. Without a shared map the position-compaction used for `responses`
 * and the decrement-by-1 used for the media maps diverge for non-contiguous detected
 * sets, detaching a surviving row's attachments from its values.
 */
export const reindexResponsesAfterDynamicGroupDeletion = (
  responses: Responses,
  groupId: string,
  groupFields: DynamicGroupType["fields"],
  deletedInstance: number,
  remainingInstances?: number[],
): Responses => {
  const survivors = (remainingInstances ?? getDynamicGroupInstances(responses, groupId)).filter(
    (instance) => instance !== deletedInstance,
  )

  const nextResponses: Responses = { ...responses }

  groupFields.forEach((groupField) => {
    delete nextResponses[`${groupId}.${deletedInstance}.${groupField.name}`]
  })

  survivors.forEach((oldInstance, index) => {
    const newInstance = index + 1
    if (oldInstance === newInstance) {
      return
    }

    groupFields.forEach((groupField) => {
      const oldKey = `${groupId}.${oldInstance}.${groupField.name}`
      const newKey = `${groupId}.${newInstance}.${groupField.name}`
      const existingValue = responses[oldKey]

      if (!existingValue) {
        return
      }

      nextResponses[newKey] = {
        ...existingValue,
        groupId,
        instance: newInstance,
      }
      delete nextResponses[oldKey]
    })
  })

  return nextResponses
}
